import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions,
} from "../../design/dialog";
import { Button } from "../../design/button";
import { Input } from "../../design/input";
import { Field, Label } from "../../design/fieldset";
import { Text } from "../../design/text";
import { TeamService } from "../../services/TeamService";

interface InviteMemberDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  onSuccess: () => void;
}

export const InviteMemberDialog: React.FC<InviteMemberDialogProps> = ({
  open,
  onClose,
  accountId,
  onSuccess,
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError("Email is required");
      return;
    }

    setLoading(true);
    try {
      await TeamService.inviteMember({
        accountId,
        channel: "email",
        email,
        intendedMemberType: "staff",
        roleIds: ["admin"],
      });

      // Reset form
      setEmail("");

      onSuccess();
    } catch (err: any) {
      setError(err.message || "Failed to send invite");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogDescription>
          Send an invitation to join your team. They will receive an email with a link to sign
          up and automatically be added as an administrator.
        </DialogDescription>

        <DialogBody className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3">
              <Text className="text-red-700 dark:text-red-300">{error}</Text>
            </div>
          )}

          <Field>
            <Label>Email Address</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="team@example.com"
              disabled={loading}
              required
            />
          </Field>
        </DialogBody>

        <DialogActions>
          <Button outline onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button color="indigo" type="submit" disabled={loading}>
            {loading ? "Sending..." : "Send Invite"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
